import { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { extractQuestionsServer } from '@/api/endpoints';
import type {
  DocumentAnchorDto,
  ServerExtractedQuestionWithTable,
  TableShapeDto,
} from '@/types/api.types';
import { extractQuestions, type ExtractedQuestion, type ExtractionResult } from '../utils/questionExtractor';
import { isExcelFile, isWordFile } from '../utils/fileValidation';

/** Lifecycle state of the file parsing operation. */
export type ParsingStatus = 'idle' | 'parsing' | 'success' | 'error';

/**
 * Return value of {@link useFileParser}.
 *
 * For .docx and .xlsx files, parsing routes to the server-side extractor,
 * which populates the anchor/shape maps alongside `questions`. PDF files use
 * the client-side extractor; in that case the anchor maps are empty.
 */
export interface UseFileParserReturn {
  status: ParsingStatus;
  questions: ExtractedQuestion[];
  error: string | null;
  metadata: ExtractionResult['metadata'] | null;
  /** DocumentAnchor per question_id (server-extracted files only, M2 F5). */
  anchorsById: Record<string, DocumentAnchorDto | null>;
  /** Table shape per question_id (server-extracted files only, Wellington #1). */
  tableShapesById: Record<string, TableShapeDto | null>;
  /** Answer-target cell anchors per question_id (server-extracted, Wellington #1). */
  answerTargetsById: Record<string, DocumentAnchorDto[] | null>;
  /** Blob ref of the stored original (server-extracted files only). */
  originalFileRef: string | null;
  /**
   * Parse a DDQ file and populate `questions`.
   *
   * .docx and .xlsx files are sent to the server extractor first; on any
   * server failure, the call falls back to the client-side extractor so the
   * upload flow continues. PDF files always use the client extractor. Sets
   * `status` to 'parsing' during the operation and 'success' or 'error' when
   * complete.
   *
   * @param file - The DDQ file to parse.
   * @returns The extraction result, including questions and metadata.
   */
  parseFile: (file: File) => Promise<ExtractionResult>;
  /**
   * Reset all parsing state to initial values.
   *
   * Clears `questions`, `error`, `metadata`, all anchor maps, and
   * `originalFileRef`. Sets `status` back to 'idle'.
   */
  reset: () => void;
}

/**
 * Manage the lifecycle of DDQ file parsing within the upload flow.
 *
 * For Word and Excel files, calls `extractQuestionsServer` and maps the
 * response into `ExtractedQuestion` records, assigning a UUID to each and
 * storing per-question anchors and table shapes for the round-trip export
 * pipeline. On server failure, falls back to `extractQuestions` (client-side).
 * PDF files always use the client-side path.
 *
 * @returns Hook state and actions for parsing a single DDQ file.
 */
export function useFileParser(): UseFileParserReturn {
  const [status, setStatus] = useState<ParsingStatus>('idle');
  const [questions, setQuestions] = useState<ExtractedQuestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<ExtractionResult['metadata'] | null>(null);
  const [anchorsById, setAnchorsById] = useState<Record<string, DocumentAnchorDto | null>>({});
  const [tableShapesById, setTableShapesById] = useState<Record<string, TableShapeDto | null>>({});
  const [answerTargetsById, setAnswerTargetsById] = useState<
    Record<string, DocumentAnchorDto[] | null>
  >({});
  const [originalFileRef, setOriginalFileRef] = useState<string | null>(null);

  const parseFile = useCallback(async (file: File): Promise<ExtractionResult> => {
    setStatus('parsing');
    setError(null);
    setAnchorsById({});
    setTableShapesById({});
    setAnswerTargetsById({});
    setOriginalFileRef(null);

    // .docx/.xlsx route to server-side extraction (M2 F1): anchors enable
    // round-trip export. PDF keeps the client path until parity. On any
    // server failure, fall back to the client extractor so upload still works.
    if (isWordFile(file) || isExcelFile(file)) {
      try {
        const server = await extractQuestionsServer(file);
        const anchors: Record<string, DocumentAnchorDto | null> = {};
        const tableShapes: Record<string, TableShapeDto | null> = {};
        const answerTargets: Record<string, DocumentAnchorDto[] | null> = {};
        const mapped: ExtractedQuestion[] = server.questions.map((rawQ) => {
          // The backend now also returns table_shape/answer_targets (Wellington #1);
          // read them via the extended type (the endpoint return type is locked).
          const q = rawQ as ServerExtractedQuestionWithTable;
          const id = uuidv4();
          anchors[id] = q.anchor;
          tableShapes[id] = q.table_shape ?? null;
          answerTargets[id] = q.answer_targets ?? null;
          return { question_id: id, question: q.text };
        });
        setQuestions(mapped);
        setAnchorsById(anchors);
        setTableShapesById(tableShapes);
        setAnswerTargetsById(answerTargets);
        setOriginalFileRef(server.original_file_ref);
        setMetadata({ totalRows: server.questions.length, sheetsProcessed: [] });
        setStatus('success');
        return {
          success: true,
          questions: mapped,
          metadata: { totalRows: server.questions.length, sheetsProcessed: [] },
        };
      } catch {
        // Fall through to the client-side extractor below.
      }
    }

    const result = await extractQuestions(file);

    if (result.success) {
      setStatus('success');
      setQuestions(result.questions);
      setMetadata(result.metadata || null);
    } else {
      setStatus('error');
      setError(result.error || 'Failed to parse file');
      setQuestions([]);
      setMetadata(null);
    }

    return result;
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setQuestions([]);
    setError(null);
    setMetadata(null);
    setAnchorsById({});
    setTableShapesById({});
    setAnswerTargetsById({});
    setOriginalFileRef(null);
  }, []);

  return {
    status,
    questions,
    error,
    metadata,
    anchorsById,
    tableShapesById,
    answerTargetsById,
    originalFileRef,
    parseFile,
    reset,
  };
}
